import { ReactNode } from "react";
import { Control, FieldError, FieldPath, FieldValues } from "react-hook-form";
import { useIntl } from "react-intl";

import AsyncSelect, { Option } from "@components/fields/AsyncSelect";
import useAsyncSearch from "@hooks/useAsyncSearch";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import {
  Article,
  ArticleContentTypeEnum,
  GetArticlesDocument,
  GetArticlesQuery,
  GetArticlesQueryVariables
} from "@typing/Generated";

type Props<FormData extends FieldValues> = {
  allowAny?: boolean;
  contentTypes?: ArticleContentTypeEnum[];
  control: Control<FormData>;
  disabled?: boolean;
  error: FieldError | undefined;
  initialOption: {
    label: string | undefined;
    value: string | null;
  };
  label?: string | ReactNode;
  name: FieldPath<FormData>;
  required?: boolean;
};

const ArticleSelect = <FormData extends FieldValues>({
  allowAny = true,
  contentTypes = [
    ArticleContentTypeEnum.my_story,
    ArticleContentTypeEnum.text,
    ArticleContentTypeEnum.video,
    ArticleContentTypeEnum.webinar
  ],
  control,
  disabled = false,
  error,
  initialOption,
  label,
  name,
  required = false
}: Props<FormData>) => {
  const intl = useIntl();

  const getArticles = useLazyQueryWithPromise<GetArticlesQuery, GetArticlesQueryVariables>(GetArticlesDocument);

  const articleAsyncSearch = useAsyncSearch<Article>({
    defaultOptions: allowAny ? [{ label: intl.formatMessage({ id: "dictionary.any" }), value: "" }] : [],
    getLabel: t => t?.title ?? "",
    getValue: t => t?.id ?? "",
    initialOption
  });

  return (
    <AsyncSelect<FormData>
      control={control}
      defaultOptions={articleAsyncSearch.defaultOptions}
      disabled={disabled}
      error={error}
      label={label}
      name={name}
      onSearch={async search =>
        articleAsyncSearch.searchOptions(
          search,
          getArticles({
            variables: {
              contentTypes,
              search
            }
          })
        )
      }
      onSelection={(option: Option) => {
        articleAsyncSearch.setCurrentOption(option);
      }}
      required={required}
      value={articleAsyncSearch.currentOption}
    />
  );
};

export default ArticleSelect;
